import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { syncDeviceDetails } from "@/utils/deviceSync";
import { deviceDetailsThrottler, deviceUpdateThrottler } from "@/utils/apiThrottling";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://ndqudoynysmynztrepwj.supabase.co";

export const useDeviceDetails = (deviceId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: device, isLoading, error } = useQuery({
    queryKey: ["device-details", deviceId],
    queryFn: async () => {
      if (!deviceId) {
        throw new Error("No device ID provided");
      }

      console.log("Fetching device details for:", deviceId);
      
      // Resolve device name to actual device ID if needed
      console.log('🔄 Resolving device ID...');
      let actualDeviceId = deviceId;
      
      try {
        const { data: resolvedId, error: resolveError } = await supabase
          .rpc('resolve_device_id', { input_id: deviceId });
        
        if (resolveError) {
          console.warn('⚠️ Error resolving device ID, using original:', resolveError);
          // Fall back to original device ID instead of throwing
        } else {
          actualDeviceId = resolvedId || deviceId;
          console.log('✅ Using device ID:', actualDeviceId, deviceId !== actualDeviceId ? `(resolved from ${deviceId})` : '');
        }
      } catch (resolveError) {
        console.warn('⚠️ Device ID resolution failed, using original:', resolveError);
        // Continue with original deviceId
      }

      // Use throttler to manage API requests
      const data = await deviceDetailsThrottler.addRequest(
        `device-details-${actualDeviceId}`,
        async () => {
          const params = new URLSearchParams();
          params.append("deviceID", actualDeviceId);

          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session?.access_token) {
            throw new Error("No session found");
          }

          const response = await fetch(`${SUPABASE_URL}/functions/v1/deviceDetails`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
            body: params,
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch device details (${response.status})`);
          }

          return await response.json();
        }
      );
      console.log("Device details response:", data);
      
      // Handle imageList validation more robustly
      if (data.imageList !== undefined && data.imageList !== null) {
        if (!Array.isArray(data.imageList)) {
          console.log("imageList is not an array, attempting to convert:", typeof data.imageList, data.imageList);
          
          // If it's an object, try to convert it to an array
          if (typeof data.imageList === 'object') {
            try {
              // Check if it's an object with numeric keys (like an array-like object)
              const keys = Object.keys(data.imageList);
              const isArrayLike = keys.every(key => !isNaN(parseInt(key)));
              
              if (isArrayLike) {
                // Convert object with numeric keys to array
                data.imageList = Object.values(data.imageList);
                console.log("Converted array-like object to array:", data.imageList);
              } else {
                // If it's a regular object, wrap it in an array
                data.imageList = [data.imageList];
                console.log("Wrapped object in array:", data.imageList);
              }
            } catch (conversionError) {
              console.error("Error converting imageList:", conversionError);
              data.imageList = [];
            }
          } else {
            // If it's not an object, set to empty array
            console.log("imageList is not an object, setting to empty array");
            data.imageList = [];
          }
        }
      } else {
        // If imageList is undefined or null, set to empty array
        data.imageList = [];
      }
      
      // Ensure imageList is always an array at this point
      if (!Array.isArray(data.imageList)) {
        console.error("Failed to convert imageList to array, defaulting to empty array");
        data.imageList = [];
      }
      
      // Sync the API response with our database
      await syncDeviceDetails(actualDeviceId, data);
      
      // After syncing, fetch the database record to get processed camera_config
      try {
        console.log("Fetching database record for device:", actualDeviceId);
        const { data: dbRecord, error: dbError } = await supabase
          .from('device_details')
          .select('*')
          .eq('device_id', actualDeviceId)
          .single();

        if (dbError) {
          console.log("Error fetching database record:", dbError, "- falling back to API response");
          return data;
        }

        if (dbRecord) {
          console.log("Database record found, camera_config:", dbRecord.camera_config);
          
          // Merge API response with database record, prioritizing database for camera_config
          const mergedData = {
            ...data,
            camera_config: dbRecord.camera_config || data.camera_config || data.cameraConfig,
            // Preserve other important database fields
            device_id: dbRecord.device_id,
            lat_wgs84: dbRecord.lat_wgs84,
            long_wgs84: dbRecord.long_wgs84,
            location: dbRecord.location || data.location,
            location_enabled: dbRecord.location_enabled,
            images_allowed: dbRecord.images_allowed,
            images_current: dbRecord.images_current,
            images_overflow: dbRecord.images_overflow,
            past_images: dbRecord.past_images || data.past_images
          };

          console.log("Merged data with camera_config:", mergedData.camera_config);
          return mergedData;
        }
      } catch (dbQueryError) {
        console.log("Database query error:", dbQueryError, "- falling back to API response");
      }
      
      return data;
    },
    enabled: !!deviceId,
    staleTime: Infinity, // Never consider data stale - only refresh manually
    refetchInterval: false, // No automatic polling
    refetchOnWindowFocus: false, // No refresh on window focus
    refetchOnMount: false, // Don't refetch on component remount
    retry: false, // Let the throttler handle retries
  });

  const updateDevice = async (field: string, value: string | number): Promise<void> => {
    if (!deviceId) {
      toast.error("No device ID provided");
      return;
    }

    try {
      console.log("Updating device:", deviceId, field, value);
      
      // Use throttler for update requests
      const updatedData = await deviceUpdateThrottler.addRequest(
        `device-update-${deviceId}-${field}`,
        async () => {
          const params = new URLSearchParams();
          params.append("deviceID", deviceId);
          params.append("field", field);
          params.append("value", value.toString());

          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session?.access_token) {
            throw new Error("No session found");
          }

          const response = await fetch(`${SUPABASE_URL}/functions/v1/updateDevice`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: params,
          });

          if (!response.ok) {
            throw new Error(`Failed to update device (${response.status})`);
          }

          return await response.json();
        }
      );
      console.log("Update response:", updatedData);

      // Sync the updated data with our database
      await syncDeviceDetails(deviceId, updatedData);

      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ["device-details", deviceId] });
      await queryClient.invalidateQueries({ queryKey: ["device", deviceId] });

      toast.success("Device updated successfully");
    } catch (error) {
      console.error("Error updating device:", error);
      toast.error("Failed to update device");
      throw error;
    }
  };

  const refreshDevice = async (): Promise<void> => {
    if (!deviceId) {
      toast.error("No device ID provided");
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["device-details", deviceId] });
    await queryClient.invalidateQueries({ queryKey: ["device", deviceId] });
  };

  return {
    device,
    isLoading,
    error,
    updateDevice,
    refreshDevice
  };
};
